import * as React from 'react';
import type { Site } from '@hotjar/site';
import { GettingStartedGuide } from '@hotjar/webapp/features/Activation/GetStartedGuide';
import { HighlightsPopup } from '@hotjar/webapp/features/Activation/HighlightsPopup';
import { useHighlightsSuggestions } from '@hotjar/webapp/features/Activation/hooks/useHighlightsSuggestions';
import { RecordingsPopup } from '@hotjar/webapp/features/Activation/RecordingsPopup';
import { useFeedbackPopup } from '@hotjar/webapp/features/Activation/FeedbackPopup/useFeedbackPopup';
import { FeedbackPopup } from '@hotjar/webapp/features/Activation/FeedbackPopup';
import { useRecordingsPopup } from '@hotjar/webapp/features/Activation/RecordingsPopup/useRecordingsPopup';
import { useDismiss } from '@hotjar/webapp/hooks/useDismiss';
import { FeedbackHeatmapsPopup } from '@hotjar/webapp/features/Activation/FeedbackHeatmapsPopup';
import { useFeedbackHeatmapsPopup } from '@hotjar/webapp/features/Activation/FeedbackHeatmapsPopup/useFeedbackHeatmapsPopup';
import { USER_GOALS } from '@hotjar/webapp/domains/activation/user-personalization-info/types';
import { useUserPersonalizationInfoQuery } from '@hotjar/webapp/domains/activation/user-personalization-info/query';
import { useGettingStartedSideBar } from '@hotjar/webapp/features/Activation/GetStartedGuide/hooks/useGettingStartedSideBar';
import { APP_STATE_NAMESPACE } from './constants';

export const FloatingSuggestions = ({ site }: { site: Site }): JSX.Element | null => {
  const { isDismissed: isHighlightsPopupDismissed, dismiss: dismissHighlights } = useDismiss(
    APP_STATE_NAMESPACE,
    'highlights',
  );
  const { isTriggered: isRecordingsPopupVisible } = useRecordingsPopup();
  const { isEnabled: isFeedbackPopupEnabled, dismiss: dismissFeedbackPopup } = useFeedbackPopup();
  const { isEnabled: isFeedbackHeatmapsPopupEnabled, dismiss: dismissFeedbackHeatmapsPopup } =
    useFeedbackHeatmapsPopup();

  const { isSideBarEnabled: isGSGSideBarEnabled } = useGettingStartedSideBar();

  const { isEnabled: shouldShowHighlightsSuggestion, highlightsLink } =
    useHighlightsSuggestions(site);
  const { data: userPersonalizationInfo } = useUserPersonalizationInfoQuery();

  const checkFeedbackPopup = () =>
    isFeedbackPopupEnabled ? (
      <FeedbackPopup key="FeedbackPopup" onDismiss={dismissFeedbackPopup} />
    ) : undefined;

  const checkFeedbackHeatmapsPopup = () =>
    isFeedbackHeatmapsPopupEnabled ? (
      <FeedbackHeatmapsPopup key="FeedbackHeatmapsPopup" onDismiss={dismissFeedbackHeatmapsPopup} />
    ) : undefined;

  const checkRecordingsPopup = () =>
    isRecordingsPopupVisible ? <RecordingsPopup key="RecordingsPopup" /> : undefined;

  const renderHighlightsOrGSG = () => (
    <React.Fragment key="HighlightsOrGSG">
      {!isHighlightsPopupDismissed && shouldShowHighlightsSuggestion ? (
        <HighlightsPopup highlightsLink={highlightsLink} onDismiss={dismissHighlights} />
      ) : (
        <>{!isGSGSideBarEnabled ? <GettingStartedGuide /> : null}</>
      )}
    </React.Fragment>
  );

  const feedbackHeatmapsPopups = [checkFeedbackPopup(), checkFeedbackHeatmapsPopup()];
  const highlightsGSGPopups = [renderHighlightsOrGSG()];

  const popupPriorityByUserGoal = {
    heatmaps: [...feedbackHeatmapsPopups, checkRecordingsPopup(), ...highlightsGSGPopups],
    default: [...feedbackHeatmapsPopups, checkRecordingsPopup(), ...highlightsGSGPopups],
  };

  // Fragments are required to use this component as JSX
  return userPersonalizationInfo && userPersonalizationInfo.goal === USER_GOALS.HEATMAPS ? (
    <>{popupPriorityByUserGoal.heatmaps}</>
  ) : (
    <>{popupPriorityByUserGoal.default}</>
  );
};
